import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Phone, Star, CheckCircle2, Heart, Shield, Sparkles, Users, Clock, Award, TrendingUp, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FAQ from '../components/FAQ';
import HowItWorks from '../components/HowItWorks';
import JsonLd from '../components/JsonLd';
import useCityForm from '../hooks/useCityForm';
import { cities, services, testimonials, phoneNumber, whatsappLink, getTestimonialAvatar } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// --- Sub-components ---

const CityHero = ({ city }) => (
  <section
    className="relative pt-32 pb-16 overflow-hidden"
    style={{
      backgroundImage: 'url(https://images.unsplash.com/photo-1711559524964-faa0d156d822?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxzcGlyaXR1YWwlMjBjYW5kbGVzfGVufDB8fHx8MTc2OTc3NTY1MXww&ixlib=rb-4.1.0&q=85)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }}
  >
    <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black"></div>
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 mb-6 px-6 py-2 bg-amber-500/20 backdrop-blur-sm rounded-full border border-amber-400/30">
          <Award className="w-5 h-5 text-amber-400" />
          <p className="text-amber-400 font-semibold">Marabout Africain Certifié</p>
        </div>
        
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent leading-tight px-4">
          Marabout Sérieux à {city.name} - Voyant Médium
        </h1>
        
        <p className="text-sm sm:text-base md:text-lg text-amber-100/90 mb-5 leading-relaxed px-4 max-w-3xl mx-auto">
          <strong className="text-amber-400">Grand marabout sérieux</strong> reconnu à {city.name} et ses environs. <span className="text-amber-400 font-bold">Spécialiste du retour affectif rapide et garanti</span>, retour de l'être aimé, mariage, envoûtement et protection spirituelle. <span className="text-red-400 font-bold">Résultats garantis en 7 jours.</span> Consultation gratuite 24/7.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
          <a href={`tel:${phoneNumber}`}>
            <Button className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-6 text-lg shadow-lg shadow-red-500/50 transform hover:scale-105 transition-all duration-300">
              <Phone className="mr-2" size={20} />
              Appeler Maintenant
            </Button>
          </a>
          <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
            <Button className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg shadow-lg shadow-green-500/50 transform hover:scale-105 transition-all duration-300">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              WhatsApp Direct
            </Button>
          </a>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-6">
          {[
            { icon: Users, value: '2400+', label: 'Clients Satisfaits', color: 'amber' },
            { icon: TrendingUp, value: '98%', label: 'Taux de Réussite', color: 'green' },
            { icon: Award, value: '25+', label: "Ans d'Expérience", color: 'amber' },
            { icon: Clock, value: '24/7', label: 'Disponibilité', color: 'green' },
          ].map((stat) => (
            <div key={stat.label} className="bg-zinc-900/50 backdrop-blur-sm border border-amber-500/20 rounded-lg p-3">
              <div className="flex items-center justify-center gap-2 mb-1">
                <stat.icon className={`w-5 h-5 text-${stat.color}-400`} />
                <span className={`text-2xl font-bold text-${stat.color}-400`}>{stat.value}</span>
              </div>
              <p className="text-xs text-amber-100/70">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-6 text-sm">
          {['Consultation Gratuite', 'Résultats garantis', 'Discrétion Absolue'].map((item) => (
            <div key={item} className="flex items-center space-x-2 text-amber-200">
              <CheckCircle2 size={18} className="text-green-500" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const CitySurroundings = ({ city }) => (
  <section className="py-10 bg-gradient-to-b from-black to-zinc-950">
    <div className="container mx-auto px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-xl md:text-2xl font-bold text-center mb-5 text-amber-400">
          Intervention à {city.name} et ses Environs
        </h2>
        <div className="flex flex-wrap justify-center gap-2 md:gap-3">
          {city.surroundingCities.map((surroundingCity) => (
            <span key={surroundingCity} className="px-3 md:px-4 py-1.5 md:py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-200 text-xs md:text-sm hover:bg-amber-500/20 transition-colors duration-300 cursor-default">
              {surroundingCity}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const CitySEOText = ({ city }) => (
  <section className="py-14 bg-zinc-950">
    <div className="container mx-auto px-4">
      <div className="max-w-5xl mx-auto">
        <div className="bg-gradient-to-br from-zinc-900/90 to-black border border-amber-600/30 rounded-2xl p-6 md:p-10 backdrop-blur-sm shadow-2xl">
          <h2 className="text-xl md:text-3xl font-bold text-amber-400 mb-6 text-center">
            Votre Marabout Sérieux de Confiance à {city.name}
          </h2>
          <div className="space-y-5 text-amber-100/80 leading-relaxed text-sm md:text-base">
            <p>Vous habitez à <strong className="text-amber-400">{city.name}</strong> ou dans les villes environnantes ({city.surroundingCities.slice(0, 4).join(', ')}) et vous cherchez un <strong className="text-amber-400">marabout sérieux</strong> pour résoudre vos problèmes sentimentaux ? Le Grand Marabout est un <strong className="text-amber-400">maître spirituel</strong>, grand marabout voyant et médium reconnu avec <strong className="text-amber-400">plus de 25 ans d'expérience</strong>. Il est considéré comme le <strong className="text-amber-400">meilleur marabout sérieux de {city.name}</strong>.</p>
            <p>Sa spécialité principale est le <strong className="text-amber-400">retour affectif rapide et garanti</strong>. Que vous souhaitiez <strong className="text-amber-400">faire revenir votre ex</strong>, <strong className="text-amber-400">récupérer l'être aimé</strong>, ou <strong className="text-amber-400">reconquérir votre partenaire</strong>, ses rituels puissants vous apporteront satisfaction en <strong className="text-red-400">7 jours maximum</strong>.</p>
            <p>Ses <strong className="text-amber-400">rituels d'envoûtement amoureux</strong> sont authentiques, transmis de génération en génération. C'est un <strong className="text-amber-400">vrai marabout sérieux</strong>, pas un charlatan.</p>
            <p>Au-delà du retour affectif, Le Grand Marabout intervient pour la <strong className="text-amber-400">protection contre le mauvais œil</strong>, le <strong className="text-amber-400">désenvoûtement</strong>, les <strong className="text-amber-400">rituels de mariage</strong>, l'<strong className="text-amber-400">attraction de la chance</strong> et la <strong className="text-amber-400">réussite professionnelle</strong>.</p>
            <p className="text-amber-300 font-semibold text-base md:text-lg border-t border-amber-600/30 pt-5 mt-6">
              Ne restez plus seul(e) face à vos problèmes sentimentaux à {city.name}. Contactez Le Grand Marabout dès maintenant pour une consultation gratuite.
            </p>
          </div>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${phoneNumber}`}>
              <Button className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-5 text-base md:text-lg shadow-lg shadow-red-500/50 transform hover:scale-105 transition-all duration-300">
                <Phone className="mr-2" size={20} /> Appeler Maintenant
              </Button>
            </a>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white px-8 py-5 text-base md:text-lg shadow-lg shadow-green-500/50 transform hover:scale-105 transition-all duration-300">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                WhatsApp Direct
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CityServices = ({ cityName }) => (
  <section id="services" className="py-10 bg-zinc-950">
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">
          Mes Services de Marabout à {cityName}
        </h2>
        <p className="text-lg text-amber-100/70 max-w-3xl mx-auto">Spécialiste des rituels traditionnels africains avec plus de 25 ans d'expérience</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {services.map((service) => (
          <Card key={service.id} className="bg-gradient-to-br from-zinc-900 to-black border-amber-600/30 hover:border-amber-500/50 transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-amber-500/20 group">
            <CardContent className="p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-amber-500/50">
                <span className="text-3xl">{service.icon}</span>
              </div>
              <h3 className="text-xl font-bold text-amber-400 mb-3">{service.title}</h3>
              <p className="text-amber-100/70 text-sm leading-relaxed">{service.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

const CityTestimonials = ({ cityName }) => (
  <section id="temoignages" className="py-10 bg-black relative overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="text-center mb-12">
        <h2 className="text-2xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">
          Témoignages WhatsApp Récents de {cityName}
        </h2>
        <p className="text-base md:text-lg text-amber-100/70">Des centaines de clients satisfaits à {cityName} et partout en France</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-7xl mx-auto">
        {testimonials.map((testimonial) => {
          const avatar = getTestimonialAvatar(testimonial.name);
          return (
            <Card key={testimonial.id} className="bg-gradient-to-br from-zinc-900/90 to-black border-amber-600/30 hover:border-amber-500/50 transition-all duration-300 transform hover:scale-105">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${avatar.gradient} rounded-full flex items-center justify-center shadow-lg`}>
                    <span className="text-white font-bold">{avatar.initials}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-amber-300">{testimonial.name}</h4>
                    <p className="text-xs text-amber-100/50">{testimonial.city} - Il y a {testimonial.date}</p>
                  </div>
                  <div className="flex items-center gap-1 bg-green-600/20 px-2 py-1 rounded-full border border-green-500/30">
                    <Check size={12} className="text-green-400" />
                    <span className="text-xs text-green-400 font-semibold">Vérifié</span>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`star-${testimonial.name}-${i}`} size={16} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-amber-100/80 text-sm leading-relaxed italic">"{testimonial.message}"</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  </section>
);

const CityWhyChoose = ({ cityName }) => (
  <section className="py-10 bg-gradient-to-b from-zinc-950 to-black">
    <div className="container mx-auto px-4">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">
          Pourquoi Choisir un Vrai Marabout à {cityName} ?
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
        {[
          { icon: Heart, title: 'Résultats Rapides', desc: 'Rituels puissants avec résultats visibles en 7 jours maximum. Retour affectif garanti ou remboursé.', gradient: 'from-red-500 to-red-700', glow: 'shadow-red-500/50' },
          { icon: Shield, title: 'Discrétion Totale', desc: 'Confidentialité absolue garantie. Vos secrets restent protégés. Consultation en toute confiance.', gradient: 'from-amber-500 to-amber-700', glow: 'shadow-amber-500/50' },
          { icon: Sparkles, title: 'Expérience Reconnue', desc: "Plus de 25 ans d'expérience et des milliers de clients satisfaits partout en France.", gradient: 'from-green-500 to-green-700', glow: 'shadow-green-500/50' },
        ].map((item) => {
          const IconComp = item.icon;
          return (
            <div key={item.title} className="text-center p-8 bg-gradient-to-br from-zinc-900 to-black border border-amber-600/30 rounded-2xl transform hover:scale-105 transition-all duration-300">
              <div className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg ${item.glow}`}>
                <IconComp size={36} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-amber-400 mb-4">{item.title}</h3>
              <p className="text-amber-100/70 leading-relaxed">{item.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const CityContactForm = ({ formData, isSubmitting, submitMessage, handleInputChange, handleSubmit }) => (
  <section id="contact" className="py-10 bg-black relative overflow-hidden">
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-3 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200 bg-clip-text text-transparent">
            Contactez-moi Maintenant
          </h2>
          <p className="text-lg text-amber-100/70">Première consultation gratuite - Réponse rapide garantie</p>
        </div>
        <Card className="bg-gradient-to-br from-zinc-900 to-black border-amber-600/30">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-amber-400 font-semibold mb-2">Votre Nom *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required className="w-full px-4 py-3 bg-black/50 border border-amber-600/30 rounded-lg text-amber-100 focus:outline-none focus:border-amber-500 transition-colors" placeholder="Entrez votre nom" />
              </div>
              <div>
                <label htmlFor="phone" className="block text-amber-400 font-semibold mb-2">Téléphone *</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} required className="w-full px-4 py-3 bg-black/50 border border-amber-600/30 rounded-lg text-amber-100 focus:outline-none focus:border-amber-500 transition-colors" placeholder="+33 6 XX XX XX XX" />
              </div>
              <div>
                <label htmlFor="message" className="block text-amber-400 font-semibold mb-2">Votre Situation *</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required rows="5" className="w-full px-4 py-3 bg-black/50 border border-amber-600/30 rounded-lg text-amber-100 focus:outline-none focus:border-amber-500 transition-colors resize-none" placeholder="Décrivez votre situation..."></textarea>
              </div>
              {submitMessage && (
                <div className={`p-4 rounded-lg ${submitMessage.includes('Merci') ? 'bg-green-500/20 border border-green-500/50' : 'bg-red-500/20 border border-red-500/50'}`}>
                  <p className="text-center text-white">{submitMessage}</p>
                </div>
              )}
              <Button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-6 text-lg font-bold shadow-lg shadow-red-500/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? 'Envoi en cours...' : 'Envoyer la Demande'}
              </Button>
            </form>
            <div className="mt-8 pt-8 border-t border-amber-600/30">
              <p className="text-center text-amber-100/70 mb-6">Ou contactez-moi directement :</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${phoneNumber}`} className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red-500/50">
                  <Phone size={20} /><span className="font-semibold">{phoneNumber}</span>
                </a>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center space-x-2 bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-green-500/50">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  <span className="font-semibold">WhatsApp</span>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

// --- Main CityPage Component ---

const CityPage = () => {
  const { citySlug } = useParams();
  const [city, setCity] = useState(null);
  const { formData, isSubmitting, submitMessage, handleInputChange, handleSubmit, setCityName } = useCityForm('');

  useEffect(() => {
    const foundCity = cities.find(c => c.slug === (citySlug || 'marseille'));
    setCity(foundCity);
    if (foundCity) {
      setCityName(foundCity.name);
    }
    window.scrollTo(0, 0);
  }, [citySlug, setCityName]);

  if (!city) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-amber-400">Chargement...</p>
        </div>
      </div>
    );
  }

  const pageTitle = `Marabout Sérieux ${city.name} ⭐ Voyant Médium - Retour Affectif Rapide Garanti`;
  const pageDescription = `Grand marabout sérieux à ${city.name} et environs (${city.surroundingCities.slice(0, 3).join(', ')}). Spécialiste retour affectif rapide garanti. Faire revenir son ex, récupérer l'être aimé. Voyant médium authentique. 2400+ clients satisfaits. Consultation gratuite 24/7.`;
  const pageKeywords = `marabout sérieux ${city.name}, marabout ${city.name}, voyant ${city.name}, médium ${city.name}, retour affectif ${city.name}, faire revenir ex ${city.name}, marabout africain ${city.name}`;

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <link rel="alternate" hrefLang="fr" href={`https://maraboutserieux.com/${city.slug}`} />
        <meta httpEquiv="content-language" content="fr" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://maraboutserieux.com/${city.slug}`} />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Marabout Sérieux" />
        <link rel="canonical" href={`https://maraboutserieux.com/${city.slug}`} />
      </Helmet>

      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://maraboutserieux.com/" },
          { "@type": "ListItem", "position": 2, "name": `Marabout ${city.name}`, "item": `https://maraboutserieux.com/${city.slug}` }
        ]
      }} />

      <JsonLd data={{
        "@context": "https://schema.org", "@type": "ProfessionalService",
        "name": `Marabout Sérieux à ${city.name}`, "url": `https://maraboutserieux.com/${city.slug}`,
        "telephone": phoneNumber,
        "description": `Grand marabout sérieux à ${city.name}. Retour affectif rapide garanti sous 7 jours.`,
        "address": { "@type": "PostalAddress", "addressLocality": city.name, "addressCountry": "FR" },
        "priceRange": "€€",
        "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "2400", "bestRating": "5", "worstRating": "1" },
        "openingHoursSpecification": { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"], "opens": "00:00", "closes": "23:59" },
        "areaServed": [{"@type": "City", "name": city.name}, ...city.surroundingCities.slice(0, 4).map(c => ({"@type": "City", "name": c}))]
      }} />

      <Header />
      <CityHero city={city} />
      <CitySurroundings city={city} />
      <CitySEOText city={city} />
      <CityServices cityName={city.name} />
      <CityTestimonials cityName={city.name} />
      <CityWhyChoose cityName={city.name} />
      <HowItWorks city={city.name} />
      <FAQ city={city.name} />
      <CityContactForm formData={formData} isSubmitting={isSubmitting} submitMessage={submitMessage} handleInputChange={handleInputChange} handleSubmit={handleSubmit} />
      <Footer />
    </div>
  );
};

export default CityPage;
